import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { getClassStudents } from "../../redux/sclassRelated/sclassHandle";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import Popup from "../../components/Popup";
import { buildApiUrl } from "../../config/api";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";
import { getClassFromSubject, getTeacherSubjects } from "../../utils/teacherSubjects";
import {
    DISCIPLINARY_OFFENSE_CATEGORIES,
    DISCIPLINARY_OUTCOMES,
    DISCIPLINARY_STATUS_CHIP,
} from "../../config/disciplinaryConstants";

const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return Number.isNaN(x.getTime()) ? "—" : x.toLocaleDateString("en-GB");
};

const STATUS_FILTER = ["All", "Pending", "Verified", "Rejected"];

const TeacherDisciplinary = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { sclassStudents } = useSelector((s) => s.sclass);
    const { homeroomClasses, homeroomClassId, hasHomeroomClass } = useFormTeacherHomeroom();
    const { activeClassId, className: activeSubjectClassName } = useTeacherTeaching();
    const homeroomTerm = useHomeroomTeacherTerm();

    const teacherId = currentUser?._id || currentUser?.id;
    const schoolId = currentUser?.school?._id || currentUser?.schoolId;

    const teachingClasses = useMemo(() => {
        const map = new Map();
        for (const subject of getTeacherSubjects(currentUser)) {
            const cls = getClassFromSubject(subject);
            const id = cls?._id || cls?.id;
            if (id && !map.has(String(id))) {
                map.set(String(id), { id: String(id), sclassName: cls.sclassName || "—" });
            }
        }
        return [...map.values()];
    }, [currentUser]);

    const classOptions = useMemo(() => {
        const map = new Map();
        for (const c of homeroomClasses) {
            const id = c._id || c.id;
            if (id) map.set(String(id), { id: String(id), sclassName: c.sclassName, isHomeroom: true });
        }
        for (const c of teachingClasses) {
            if (!map.has(c.id)) {
                map.set(c.id, { id: c.id, sclassName: c.sclassName, isHomeroom: false });
            }
        }
        return [...map.values()];
    }, [homeroomClasses, teachingClasses]);

    const [selectedClassId, setSelectedClassId] = useState("");
    const [listTab, setListTab] = useState(hasHomeroomClass ? "homeroom" : "mine");
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState("All");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [reportOpen, setReportOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [studentId, setStudentId] = useState("");
    const [offenseCategory, setOffenseCategory] = useState(DISCIPLINARY_OFFENSE_CATEGORIES[0]);
    const [incidentDate, setIncidentDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [incidentDescription, setIncidentDescription] = useState("");
    const [recommendedOutcome, setRecommendedOutcome] = useState("");

    useEffect(() => {
        if (!selectedClassId && classOptions.length) {
            const preferred =
                homeroomClassId && classOptions.some((c) => c.id === homeroomClassId)
                    ? homeroomClassId
                    : activeClassId && classOptions.some((c) => c.id === activeClassId)
                      ? activeClassId
                      : classOptions[0].id;
            setSelectedClassId(preferred);
        }
    }, [selectedClassId, classOptions, homeroomClassId, activeClassId]);

    const selectedClass = useMemo(
        () => classOptions.find((c) => c.id === selectedClassId) || null,
        [classOptions, selectedClassId]
    );

    const isFormTeacherForSelected =
        hasHomeroomClass && homeroomClasses.some((c) => (c._id || c.id) === selectedClassId);

    const canReportForSelected = Boolean(selectedClassId && (isFormTeacherForSelected || teachingClasses.some((c) => c.id === selectedClassId)));

    const loadCases = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== "All") params.set("status", statusFilter);
            if (listTab === "homeroom" && selectedClassId) {
                params.set("view", "homeroom");
                params.set("classId", selectedClassId);
            } else if (selectedClassId) {
                params.set("classId", selectedClassId);
            }
            const query = params.toString() ? `?${params.toString()}` : "";
            const { data } = await axios.get(
                buildApiUrl(`/DisciplinaryCases/teacher/${teacherId}${query}`)
            );
            setRows(Array.isArray(data) ? data : []);
        } catch (err) {
            const apiMsg = err.response?.data?.message;
            const detail = typeof apiMsg === "string" ? apiMsg : err.response?.data?.error;
            setMessage(detail || apiMsg || "Could not load disciplinary cases");
            setShowPopup(true);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [teacherId, statusFilter, listTab, selectedClassId]);

    useEffect(() => {
        loadCases();
    }, [loadCases]);

    useEffect(() => {
        if (selectedClassId) dispatch(getClassStudents(selectedClassId));
    }, [dispatch, selectedClassId]);

    const studentOptions = useMemo(() => {
        if (!Array.isArray(sclassStudents)) return [];
        return sclassStudents.map((s) => ({
            id: s._id || s.id,
            label: `${s.name} (Roll ${s.rollNum})`,
        }));
    }, [sclassStudents]);

    const pendingCount = useMemo(() => rows.filter((r) => r.status === "Pending").length, [rows]);

    const resetReportForm = () => {
        setStudentId("");
        setOffenseCategory(DISCIPLINARY_OFFENSE_CATEGORIES[0]);
        setIncidentDate(new Date().toISOString().slice(0, 10));
        setIncidentDescription("");
        setRecommendedOutcome("");
    };

    const handleSubmitReport = async () => {
        if (!studentId || !incidentDescription.trim() || !selectedClassId) {
            setMessage("Select a class, student, and describe the incident");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const { data } = await axios.post(buildApiUrl("/DisciplinaryCase"), {
                schoolId,
                studentId,
                classId: selectedClassId,
                reportedByTeacherId: teacherId,
                incidentDate,
                offenseCategory,
                incidentDescription: incidentDescription.trim(),
                recommendedOutcome,
                academicYear: String(new Date().getFullYear()),
                term: "",
            });
            if (data?.message && !data?.id && !data?._id) {
                setMessage(data.message);
                setShowPopup(true);
                return;
            }
            setMessage(
                isFormTeacherForSelected
                    ? "Case submitted — awaiting director verification"
                    : `Case reported to the ${homeroomTerm.theRole} and director for verification`
            );
            setShowPopup(true);
            setReportOpen(false);
            resetReportForm();
            await loadCases();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not submit case");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    if (!classOptions.length) {
        return (
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    No teaching class is linked to your account. Ask the director to assign your subjects
                    or form class before recording disciplinary cases.
                </Alert>
                <Button component={RouterLink} to="/Teacher/dashboard" sx={{ mt: 2 }}>
                    Back to dashboard
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Button component={RouterLink} to="/Teacher/dashboard" startIcon={<ArrowBackIcon />} size="small">
                    Dashboard
                </Button>
            </Stack>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Disciplinary cases
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Report student misconduct — {homeroomTerm.theRole}s see all class reports; the
                        director verifies outcomes
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Class</InputLabel>
                        <Select
                            label="Class"
                            value={selectedClassId}
                            onChange={(e) => setSelectedClassId(e.target.value)}
                        >
                            {classOptions.map((c) => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.sclassName}
                                    {c.isHomeroom ? ` (${homeroomTerm.roleTitleCaps})` : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            {STATUS_FILTER.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        startIcon={<GavelOutlinedIcon />}
                        disabled={!canReportForSelected}
                        onClick={() => {
                            resetReportForm();
                            setReportOpen(true);
                        }}
                    >
                        Report incident
                    </Button>
                </Stack>
            </Stack>

            {hasHomeroomClass ? (
                <Tabs
                    value={listTab}
                    onChange={(_, v) => setListTab(v)}
                    sx={{ mb: 2 }}
                    variant="scrollable"
                    allowScrollButtonsMobile
                >
                    <Tab
                        value="homeroom"
                        label={`Class reports (${selectedClass?.sclassName || homeroomTerm.roleTitleCaps})`}
                    />
                    <Tab value="mine" label="My reports" />
                </Tabs>
            ) : null}

            {!canReportForSelected && selectedClassId ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    You can view reports for this class but cannot file new ones unless you teach it.
                    Active subject class: {activeSubjectClassName || "—"}.
                </Alert>
            ) : null}

            {listTab === "homeroom" && pendingCount > 0 ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    {pendingCount} case(s) from teachers awaiting director decision.
                </Alert>
            ) : null}

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Student</StyledTableCell>
                                {listTab === "homeroom" ? (
                                    <StyledTableCell>Reported by</StyledTableCell>
                                ) : null}
                                <StyledTableCell>Offense</StyledTableCell>
                                <StyledTableCell>Summary</StyledTableCell>
                                <StyledTableCell>Suggested</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell>Outcome</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={listTab === "homeroom" ? 8 : 7}>
                                        <Skeleton height={48} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : rows.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell
                                        colSpan={listTab === "homeroom" ? 8 : 7}
                                        align="center"
                                        sx={{ py: 4 }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            No disciplinary cases recorded yet.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                rows.map((row) => {
                                    const id = row.id || row._id;
                                    const status = row.status || "Pending";
                                    return (
                                        <StyledTableRow key={id} hover>
                                            <StyledTableCell>{formatDate(row.incidentDate)}</StyledTableCell>
                                            <StyledTableCell>
                                                {row.student?.name || "—"}
                                                {row.student?.rollNum != null
                                                    ? ` (#${row.student.rollNum})`
                                                    : ""}
                                            </StyledTableCell>
                                            {listTab === "homeroom" ? (
                                                <StyledTableCell>
                                                    {row.reportedByTeacher?.name || "—"}
                                                </StyledTableCell>
                                            ) : null}
                                            <StyledTableCell>{row.offenseCategory}</StyledTableCell>
                                            <StyledTableCell>
                                                {String(row.incidentDescription || "").slice(0, 80)}
                                                {String(row.incidentDescription || "").length > 80
                                                    ? "…"
                                                    : ""}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row.recommendedOutcome || "—"}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    label={status}
                                                    size="small"
                                                    color={DISCIPLINARY_STATUS_CHIP[status] || "default"}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {row.outcome || "—"}
                                                {row.status === "Rejected" && row.rejectionNote ? (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        color="error"
                                                    >
                                                        {row.rejectionNote}
                                                    </Typography>
                                                ) : null}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Report disciplinary incident</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            {isFormTeacherForSelected
                                ? "The director will verify and decide: strong warning, suspension, or expulsion."
                                : `This report is sent to your ${homeroomTerm.theRole} and the director for verification.`}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Class: <strong>{selectedClass?.sclassName || "—"}</strong>
                        </Typography>
                        <FormControl fullWidth required>
                            <InputLabel>Student</InputLabel>
                            <Select
                                label="Student"
                                value={studentId}
                                onChange={(e) => setStudentId(e.target.value)}
                            >
                                {studentOptions.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth required>
                            <InputLabel>Offense category</InputLabel>
                            <Select
                                label="Offense category"
                                value={offenseCategory}
                                onChange={(e) => setOffenseCategory(e.target.value)}
                            >
                                {DISCIPLINARY_OFFENSE_CATEGORIES.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            required
                            type="date"
                            label="Incident date"
                            value={incidentDate}
                            onChange={(e) => setIncidentDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            required
                            multiline
                            minRows={4}
                            label="What happened"
                            value={incidentDescription}
                            onChange={(e) => setIncidentDescription(e.target.value)}
                            placeholder="Describe the offense, witnesses, and context…"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Suggested action (optional)</InputLabel>
                            <Select
                                label="Suggested action (optional)"
                                value={recommendedOutcome}
                                onChange={(e) => setRecommendedOutcome(e.target.value)}
                            >
                                <MenuItem value="">No suggestion</MenuItem>
                                {DISCIPLINARY_OUTCOMES.map((o) => (
                                    <MenuItem key={o} value={o}>
                                        {o}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReportOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmitReport} disabled={saving}>
                        Submit for verification
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} showPopup={showPopup} setShowPopup={setShowPopup} />
        </Container>
    );
};

export default TeacherDisciplinary;
